import { memo, useMemo, useState } from 'react'
import type { Series } from '../../types/domain.js'
import { seriesApi } from '../../lib/api.js'
import { confirmSuggestedProgramMedia } from '../../lib/program-media-suggestions.js'
import { ModalShell } from '../common/ModalShell.js'

/**
 * Création d'une édition dans une série.
 *
 * Extraite de l'ancien `SeriesPanel` lors de la refonte de l'écran Séries : la
 * modale survit au panneau qui l'hébergeait, et le workbench l'ouvre aussi bien
 * depuis la liste des séries que depuis la saison courante.
 */

export const NewSeriesEpisodeModal = memo(function NewSeriesEpisodeModal({ series, onCreated, onClose }: {
  series: Series
  onCreated: () => void
  onClose: () => void
}) {
  // Numérotation proposée : suite de la saison la plus élevée déjà en service.
  // Rien n'est déduit de l'année ; laisser les champs vides crée une édition
  // sans numéro, ce qui reste un choix valable.
  const suggested = useMemo(() => {
    if (!series.numberEpisodes) return { season: '', episode: '' }
    const episodes = series.programs ?? []
    const seasons = episodes.map(e => e.season).filter((value): value is number => value != null)
    if (seasons.length === 0) return { season: '', episode: '' }
    const season = Math.max(...seasons)
    const numbers = episodes
      .filter(e => e.season === season)
      .map(e => e.episode)
      .filter((value): value is number => value != null)
    return { season: String(season), episode: String(numbers.length ? Math.max(...numbers) + 1 : 1) }
  }, [series.programs, series.numberEpisodes])

  const [date, setDate] = useState(new Date().toLocaleDateString('sv-SE'))
  const [title, setTitle] = useState('')
  const [season, setSeason] = useState(suggested.season)
  const [episode, setEpisode] = useState(suggested.episode)
  const [partDurationsMin, setPartDurationsMin] = useState(
    series.defaultPartDurationsSec.length ? series.defaultPartDurationsSec.map(value => String(value / 60)) : ['52'],
  )
  const [breakMin, setBreakMin] = useState(String(series.defaultInterPartBreakSec / 60))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setSaving(true)
    setError(null)
    try {
      const program = await seriesApi.createEdition(series.id, {
        plannedFirstAirDate: date,
        title: title.trim() || undefined,
        season: season ? Number(season) : undefined,
        episode: episode ? Number(episode) : undefined,
        partDurationsSec: partDurationsMin.map(value => Math.max(1, Math.round(Number(value || 1) * 60))),
        interPartBreakSec: Math.max(0, Math.round(Number(breakMin || 0) * 60)),
      })
      await confirmSuggestedProgramMedia(program.id).catch(() => null)
      onCreated()
    } catch (reason: any) {
      setError(reason.message)
      setSaving(false)
    }
  }

  return (
    <ModalShell title="Nouvel épisode" onClose={onClose}>
      <form onSubmit={submit} className="tp-modal-form">
        <div className="catalog-modal-description">Profil copié depuis « {series.title} »</div>
        {error && <div className="tp-feedback tp-feedback--error" role="alert">{error}</div>}
        <div className={series.numberEpisodes ? 'tp-form-grid catalog-episode-identity-grid' : 'tp-form-grid'}>
          <label className="tp-form-field"><span className="tp-field-label">Date prévue *</span><input required type="date" value={date} onChange={e => setDate(e.target.value)} className="tp-input" /></label>
          {/* Série sans numérotation : les champs saison/épisode n'ont pas lieu
              d'être proposés, le serveur les ignorerait de toute façon. */}
          {series.numberEpisodes && <>
            <label className="tp-form-field"><span className="tp-field-label">Saison</span><input type="number" min="1" value={season} onChange={e => setSeason(e.target.value)} className="tp-input" /></label>
            <label className="tp-form-field"><span className="tp-field-label">Épisode</span><input type="number" min="1" value={episode} onChange={e => setEpisode(e.target.value)} className="tp-input" /></label>
          </>}
        </div>
        <label className="tp-form-field"><span className="tp-field-label">Titre spécifique</span><input value={title} onChange={e => setTitle(e.target.value)} className="tp-input" placeholder="Généré automatiquement si vide" /></label>
        <div className="tp-form-grid tp-form-grid--three">
          {partDurationsMin.map((value, index) => (
            <label key={index} className="tp-form-field"><span className="tp-field-label">Partie {index + 1} (min)</span><input type="number" min="1" value={value} onChange={e => setPartDurationsMin(parts => parts.map((part, partIndex) => partIndex === index ? e.target.value : part))} className="tp-input" /></label>
          ))}
          {partDurationsMin.length > 1 && <label className="tp-form-field"><span className="tp-field-label">Coupure (min)</span><input type="number" min="0" step="0.5" value={breakMin} onChange={e => setBreakMin(e.target.value)} className="tp-input" /></label>}
        </div>
        <div className="tp-modal-actions">
          <button type="button" onClick={onClose} className="tp-button">Annuler</button>
          <button type="submit" disabled={saving} className="tp-button tp-button--info" data-state={saving ? 'loading' : undefined}>{saving ? 'Création…' : 'Créer'}</button>
        </div>
      </form>
    </ModalShell>
  )
})
